import { useCallback, useState } from "react";
import { ROLE_LABEL, Shell } from "@/app/Shell";
import { TabBar } from "@/app/TabBar";
import { useDashboard, useRoute, type StepView } from "@/app/queries";
import { useMe } from "@/app/session";
import { NotFound, SkeletonRows } from "@/ui/states";
import { Segments } from "@/ui/Segments";
import { ProgressRing } from "@/ui/Progress";
import { NetworkError } from "./route/bits";
import { mainTracks, parallelTracks, trackLead, trackTone } from "./route/chain";
import { LockedStepRow, StepRow } from "./route/StepRow";
import { TrackAside } from "./route/TrackAside";

/** S3 «Трек целиком»: шапка с сегментами и кольцом гейтов, аккордеон шагов,
 *  справа место в маршруте и статистика по треку. */
export function TrackPage({ slug }: { slug: string }) {
  const route = useRoute();
  const me = useMe();
  const dash = useDashboard();
  const [opened, setOpened] = useState<Record<string, boolean>>({});
  const [views, setViews] = useState<Record<string, StepView | undefined>>({});
  const onView = useCallback((id: string, view: StepView | undefined) => {
    setViews((cur) => (cur[id] === view ? cur : { ...cur, [id]: view }));
  }, []);
  // Строка передаёт текущее состояние: активный шаг открыт по умолчанию без записи в opened.
  const toggle = useCallback((id: string, open: boolean) => setOpened((cur) => ({ ...cur, [id]: !open })), []);

  const user = me.data ? { name: me.data.name, role: ROLE_LABEL[me.data.role] ?? me.data.role } : undefined;
  const metaParts = [dash.data?.cohort && `когорта ${dash.data.cohort}`, dash.data?.mentor && `ментор ${dash.data.mentor}`].filter(Boolean);
  const meta = metaParts.length > 0 ? metaParts.join(" · ") : undefined;

  if (route.status === "pending") {
    return (
      <Shell nav user={user} meta={meta}>
        <SkeletonRows rows={6} />
      </Shell>
    );
  }
  if (route.status === "error") {
    return (
      <Shell nav user={user} meta={meta}>
        <NetworkError retry={() => void route.refetch()} />
      </Shell>
    );
  }
  const r = route.data;
  const track = r.tracks.find((t) => t.slug === slug);
  if (!track) {
    return (
      <Shell nav user={user} meta={meta}>
        <NotFound primary={{ label: "к карте маршрута", href: "/map" }} />
      </Shell>
    );
  }
  const tone = trackTone(track);
  const activeId = track.steps.find((s) => s.state === "active")?.id;
  const isOpen = (id: string) => opened[id] ?? id === activeId;
  const unlocked = track.steps.filter((s) => s.state !== "locked");
  const loaded = unlocked.map((s) => views[s.id]).filter((v): v is StepView => v !== undefined);
  const ringColor = tone === "done" ? "var(--ok)" : tone === "active" ? "var(--acc)" : "var(--bd2)";

  return (
    <Shell nav user={user} meta={meta}>
      <div className="grid gap-8 md:grid-cols-[minmax(0,1fr)_300px] md:gap-10">
        <div className="min-w-0">
          <header className="grid gap-6 border-b border-bd pb-[22px] md:grid-cols-[minmax(0,1fr)_104px]">
            <div className="min-w-0">
              <div className="mb-2.5 font-mono text-[11px] uppercase tracking-[.06em] text-mut">маршрут / {track.name.toLowerCase()}</div>
              <h1 className="m-0 font-mono text-[28px] font-bold leading-[1.1] tracking-[-.02em] text-ink">{track.name}</h1>
              <p className="mb-4 mt-2.5 max-w-[620px] text-[13px] leading-[1.55] text-ink2">{trackLead(track)}</p>
              <Segments total={track.total} done={track.done} active={tone === "active"} />
            </div>
            <ProgressRing done={track.done} total={track.total} size={88} stroke={7} color={ringColor} caption="гейтов сдано" />
          </header>
          <div className="mt-5 overflow-hidden rounded-lg border border-bd" data-testid="steps">
            {track.steps.map((s) =>
              s.state === "locked" ? <LockedStepRow key={s.id} step={s} /> : <StepRow key={s.id} step={s} open={isOpen(s.id)} onToggle={toggle} onView={onView} />,
            )}
          </div>
        </div>
        <TrackAside track={track} main={mainTracks(r)} parallel={parallelTracks(r)} views={loaded} loading={loaded.length < unlocked.length} />
      </div>
      <TabBar />
    </Shell>
  );
}
